export interface MmpRate {
  rateId: number;
  rateCollectionId: number;
  planCost: number;
  planCost_Discounted: number;
  fixedRetailAmount: number;
  liftedVehicleValue: number;
  surchargeListId: number;
  coverageName: string;
  coverageMonths: number;
  coverageMiles: number;
  coverageDeductible: number;
  coverageDeductibleId: number;
  isNewVehicleCoverage: boolean;
  taxAmount: number;
  taxAmount_Discounted: number;
  extendedEligSelected: boolean;
  extendedEligAmount: number;
  optionsSelected: string;
  selectedLiftKitOptionId: string;
  coverageRank: number;
  powerWrap: boolean;
  planOptions: PlanOption[];
  discountAmount: number;
  perPayment: number;
  daily: number;
  discountedPerPayment: number;
  discountedDailyAmount: number;
}

export interface PpmRate {
  totalPremium: number;
  rateId: number;
  rateCollectionId: number;
  coverageName: string;
  perPayment: number;
  daily: number;
  errorMessage: string;
  ppmTerm: number;
  ineligibleIfMMPSelected: boolean;
  discountedDailyAmount: number;
  discountAmount: number;
  discountedPerPayment: number;
  discountedPremium: number;
}

export interface SdRate {
  totalPremium: number;
  rateId: number;
  rateCollectionId: number;
  coverageDescription: string;
  coverageMonths: number;
  coverageMiles: number;
  coveragePremium: number;
  perPayment: number;
  daily: number;
  errorMessage: string;
  totalEstimatedAmount?: number;
}

export interface KeyProtectionRate {
  totalPremium: number;
  rateId: number;
  rateCollectionId: number;
  coverageDescription: string;
  coverageMonths: number;
  coverageMiles: number;
  coveragePremium: number;
  perPayment: number;
  daily: number;
  errorMessage: string;
  totalEstimatedAmount?: number;
}

export interface CvpRate {
  totalPremium: number;
  rateId: number;
  rateCollectionId: number;
  coverageDescription: string;
  coverageMonths: number;
  coverageMiles: number;
  coveragePremium: number;
  perPayment: number;
  daily: number;
  errorMessage: string;
  totalEstimatedAmount?: number;
}

export interface PlanOption {
  id: string;
  adjustID: string;
  deductible: boolean;
  liftKit: boolean;
  isCommercial: boolean;
  isModified: boolean;
  isSalvageTitle: boolean;
  isTotalLossRefund: boolean;
  deductibleAmt: number;
  optionDescription: string;
  mileageMax: number;
  selected: boolean;
  optionCost: number;
  optionDaily?: number;
  optionPerPayment?: number;
  totalOptionCostAmount?: number;
  surchargeCode: string;
  optionCostTaxAmount: number;
}

export interface GapRate {
  totalPremium: number;
  discountedPremium: number;
  rateId: number;
  rateCollectionId: number;
  coverageName: string;
  perPayment: number;
  daily: number;
  discountedPerPayment: number;
  discountedDailyAmount: number;
  discountAmount: number;
  errorMessage: string;
  multiCoverageId: number;
}

export interface PepRate {
  benefitLevel: number;
  coverageName: string;
  daily: number;
  errorMessage: null | string;
  multiCoverageId: number;
  pepTerm: number;
  perPayment: number;
  rateCollectionId: number;
  rateId: number;
  totalPremium: number;
  discountedPremium: number;
  discountedPerPayment: number;
  discountedDailyAmount: number;
  discountAmount: number;
}

export interface VehicleReturnRate {
  totalPremium: number;
  rateId: number;
  rateCollectionId: number;
  coverageName: string;
  perPayment: number;
  daily: number;
  errorMessage: string;
}

export interface PaymentProtectionRate {
  totalEstimatedAmount: number;
  creditLifeAmount: number;
  disabilityAmount: number;
  rateId: number;
  rateCollectionId: number;
  coverageName: string;
  perPayment: number;
  daily: number;
  displayPlanRate: boolean;
  errorMessage: string;
  maxLifeIssueAge: number;
  maxLifeTerm: number;
  maxLifeTermAge: number;
  maxLifeBenefit: number;
  maxDisabilityIssueAge: number;
  maxDisabilityTerm: number;
  maxDisabilityTermAge: number;
  maxDisabilityBenefit: number;
  disabilityRate: number;
  lifeRate: number;
}

export interface IRateResponse {
  message: string;
  mmpExclusionText?: string;
  mmpRates: MmpRate[];
  ppmRates: PpmRate[];
  keyProtectionRates: KeyProtectionRate[];
  pepRates?: PepRate[];
  gapRates: GapRate[];
  sdRates: SdRate[];
  vehicleReturnRates: VehicleReturnRate[];
  cvpRates: CvpRate[];
  paymentProtectionRates: PaymentProtectionRate[];
  penTrackKey: number;
}

export interface IExistingRatesResponse extends IRateResponse {
  selectedMmpRateId: number;
  selectedPpmRateId: number;
  selectedKpRateId: number;
  selectedGapRateId: number;
  selectedPepRateId: number;
  selectedVrRateId: number;
  selectedSdRateId: number;
  selectedPpiRateId: number;
  selectedCvpRateId: number;
}

export interface ProductSelectionRateCollectionId {
  isSelected: boolean;
  rateCollectionId: number;
}

export interface MmpProductSelectionRateCollectionId
  extends ProductSelectionRateCollectionId {
  isExcluded: boolean;
}

export interface RateCollectionRequest {
  portalAccountId: number;
  mmp: MmpProductSelectionRateCollectionId;
  ppm: ProductSelectionRateCollectionId;
  gap: ProductSelectionRateCollectionId;
  vehicleReturn: ProductSelectionRateCollectionId;
  paymentProtection: ProductSelectionRateCollectionId;
  pep: ProductSelectionRateCollectionId;
  sd: ProductSelectionRateCollectionId;
  keyProtection: ProductSelectionRateCollectionId;
  cvp: ProductSelectionRateCollectionId;
}

export function getEmptyRateCollectionRequest(): RateCollectionRequest {
  return {
    portalAccountId: null,
    mmp: null,
    ppm: null,
    gap: null,
    vehicleReturn: null,
    paymentProtection: null,
    pep: null,
    sd: null,
    keyProtection: null,
    cvp: null
  };
}

export function getEmptyProductSelectionRateCollectionId(): ProductSelectionRateCollectionId {
  return {
    isSelected: null,
    rateCollectionId: null
  };
}

export function getEmptyRateResponse(): IRateResponse {
  return {
    message: null,
    mmpRates: [],
    ppmRates: [],
    keyProtectionRates: [],
    gapRates: [],
    pepRates: [],
    sdRates: [],
    vehicleReturnRates: [],
    cvpRates: [],
    paymentProtectionRates: [],
    penTrackKey: null
  };
}

export type UProductRates =
  | MmpRate[]
  | PpmRate[]
  | GapRate[]
  | PepRate[]
  | VehicleReturnRate[]
  | PaymentProtectionRate[]
  | SdRate[]
  | KeyProtectionRate[]
  | CvpRate[];
export type UProductRate =
  | MmpRate
  | PpmRate
  | GapRate
  | PepRate
  | VehicleReturnRate
  | PaymentProtectionRate
  | SdRate
  | KeyProtectionRate
  | CvpRate;

export interface ICurrentSelectedProductRates {
  mmpRate: MmpRate;
  ppmRate: PpmRate;
  keyProtectionRate: KeyProtectionRate;
  gapRate: GapRate;
  pepRate: PepRate;
  vehicleReturnRate: VehicleReturnRate;
  sdRate: SdRate;
  paymentProtectionRate: PaymentProtectionRate;
  cvpRate: CvpRate;
}

export function getEmptyCurrentSelectedProductRates(): ICurrentSelectedProductRates {
  return {
    mmpRate: null,
    ppmRate: null,
    keyProtectionRate: null,
    gapRate: null,
    pepRate: null,
    vehicleReturnRate: null,
    sdRate: null,
    paymentProtectionRate: null,
    cvpRate: null
  };
}
